/** @format */

import React, { useEffect, useMemo, useRef } from "react";
import { Box } from "./MuiCompat";
import Face2OutlinedIcon from "@mui/icons-material/Face2Outlined";
import Face3OutlinedIcon from "@mui/icons-material/Face3Outlined";
import Face4OutlinedIcon from "@mui/icons-material/Face4Outlined";
import Face5OutlinedIcon from "@mui/icons-material/Face5Outlined";
import Face6OutlinedIcon from "@mui/icons-material/Face6Outlined";
import WatchOutlinedIcon from "@mui/icons-material/WatchOutlined";
import Diversity3OutlinedIcon from "@mui/icons-material/Diversity3Outlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";

const fontInter = `'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif`;

// =====================================================================
// Mesh — a perturbed-grid network of typed nodes (mostly users + sparse
// wearables / peers / brands / agents). Cursor pulls nodes via canvas
// physics, brightens edges via a flashlight effect, and the HTML icon
// overlay is repositioned per-frame via DOM refs to track the pull.
// =====================================================================

type IconCmp = React.ComponentType<{
  sx?: object;
  fontSize?: "inherit" | "small" | "medium" | "large";
}>;

interface MeshNode {
  pctX: number;
  pctY: number;
  restX: number;
  restY: number;
  x: number;
  y: number;
  Icon: IconCmp;
  isStar: boolean;
}

const COLS = 12;
const ROWS = 7;

// Identity distribution. Mostly users (mixed across Face2–6 for visible
// gender variety), with a sparse mix of stakeholders so the mesh reads as
// a real ecosystem rather than a pattern.
//   user       (Face2 – Face6) ~70 %
//   wearable   (Watch)         ~12 %
//   peer       (Diversity3)     ~ 8 %
//   brand      (Build)          ~ 6 %
//   agent      (AutoAwesome)    ~ 4 %
const FACE_ICONS: IconCmp[] = [
  Face2OutlinedIcon,
  Face3OutlinedIcon,
  Face4OutlinedIcon,
  Face5OutlinedIcon,
  Face6OutlinedIcon,
];

const generateNodes = (): MeshNode[] => {
  let s = 1337;
  const rand = () => {
    s = (s * 1664525 + 1013904223) % 4294967296;
    return s / 4294967296;
  };

  const pickIcon = (): IconCmp => {
    const r = rand() * 100;
    if (r < 4) return AutoAwesomeOutlinedIcon;
    if (r < 10) return BuildOutlinedIcon;
    if (r < 18) return Diversity3OutlinedIcon;
    if (r < 30) return WatchOutlinedIcon;
    // Pick a random face variant for users so the population reads as
    // mixed-gender / mixed-identity rather than 60 copies of the same face.
    const faceIdx = Math.floor(rand() * FACE_ICONS.length);
    return FACE_ICONS[faceIdx];
  };

  const cellW = 100 / (COLS - 1);
  const cellH = 100 / (ROWS - 1);
  const out: MeshNode[] = [];
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const jx = (rand() - 0.5) * cellW * 0.5;
      const jy = (rand() - 0.5) * cellH * 0.55;
      const pctX = Math.max(2, Math.min(98, c * cellW + jx));
      const pctY = Math.max(2, Math.min(98, r * cellH + jy));
      out.push({
        pctX,
        pctY,
        restX: 0,
        restY: 0,
        x: 0,
        y: 0,
        Icon: pickIcon(),
        isStar: rand() < 0.12,
      });
    }
  }
  return out;
};

const MeshCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cursorRef = useRef<{ x: number; y: number; active: boolean }>({
    x: -9999,
    y: -9999,
    active: false,
  });
  const rafRef = useRef<number | null>(null);
  const iconRefs = useRef<Array<HTMLDivElement | null>>([]);

  const meshNodes = useMemo(() => generateNodes(), []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = 0;
    let height = 0;

    // Mutable working copy — pixel positions get updated each frame.
    const nodes: MeshNode[] = meshNodes.map((n) => ({ ...n }));
    let edges: Array<[number, number]> = [];

    const ATTRACT_RADIUS = 220;
    const ATTRACT_STRENGTH = 0.5;
    const LERP = 0.14;

    let inView = true;
    let pageVisible =
      typeof document === "undefined" ? true : !document.hidden;
    const isActive = () => inView && pageVisible;

    const MOVE_THROTTLE_MS = 30;
    let lastMoveTs = 0;
    const REST_EPSILON = 0.05;

    const seed = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Resolve pct → px for canvas physics.
      for (let i = 0; i < nodes.length; i++) {
        nodes[i].restX = (nodes[i].pctX / 100) * width;
        nodes[i].restY = (nodes[i].pctY / 100) * height;
        nodes[i].x = nodes[i].restX;
        nodes[i].y = nodes[i].restY;
      }

      edges = [];
      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
          const i = r * COLS + c;
          if (c + 1 < COLS) edges.push([i, i + 1]);
          if (r + 1 < ROWS) edges.push([i, i + COLS]);
          if (r + 1 < ROWS && c + 1 < COLS) {
            if ((r + c) % 2 === 0) edges.push([i, i + COLS + 1]);
            else edges.push([i + 1, i + COLS]);
          }
        }
      }
    };

    const ensureRunning = () => {
      if (rafRef.current == null && isActive()) {
        rafRef.current = requestAnimationFrame(draw);
      }
    };

    const onMove = (e: MouseEvent) => {
      const now = performance.now();
      if (now - lastMoveTs < MOVE_THROTTLE_MS) return;
      lastMoveTs = now;
      const rect = canvas.getBoundingClientRect();
      cursorRef.current.x = e.clientX - rect.left;
      cursorRef.current.y = e.clientY - rect.top;
      cursorRef.current.active = true;
      ensureRunning();
    };
    const onLeave = () => {
      cursorRef.current.active = false;
      ensureRunning();
    };
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 0) return;
      const now = performance.now();
      if (now - lastMoveTs < MOVE_THROTTLE_MS) return;
      lastMoveTs = now;
      const rect = canvas.getBoundingClientRect();
      cursorRef.current.x = e.touches[0].clientX - rect.left;
      cursorRef.current.y = e.touches[0].clientY - rect.top;
      cursorRef.current.active = true;
      ensureRunning();
    };
    const onTouchEnd = () => {
      cursorRef.current.active = false;
      ensureRunning();
    };

    const draw = () => {
      if (!isActive()) {
        rafRef.current = null;
        return;
      }

      ctx.clearRect(0, 0, width, height);

      const cursor = cursorRef.current;
      const lerp = reduceMotion ? 1 : LERP;

      let anyMoving = false;
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        let tx = n.restX;
        let ty = n.restY;

        if (cursor.active) {
          const dx = cursor.x - n.restX;
          const dy = cursor.y - n.restY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < ATTRACT_RADIUS) {
            const falloff = 1 - dist / ATTRACT_RADIUS;
            const force = ATTRACT_STRENGTH * falloff * falloff;
            tx = n.restX + dx * force;
            ty = n.restY + dy * force;
          }
        }

        n.x += (tx - n.x) * lerp;
        n.y += (ty - n.y) * lerp;

        if (
          Math.abs(n.x - n.restX) > REST_EPSILON ||
          Math.abs(n.y - n.restY) > REST_EPSILON
        ) {
          anyMoving = true;
        }

        // Apply the pull to the HTML icon via direct DOM mutation.
        const iconEl = iconRefs.current[i];
        if (iconEl) {
          const offX = n.x - n.restX;
          const offY = n.y - n.restY;
          iconEl.style.transform =
            `translate3d(calc(-50% + ${offX.toFixed(2)}px), calc(-50% + ${offY.toFixed(2)}px), 0)`;
        }
      }

      // Edges. Cursor flashlight brightens edges within radius via cubic
      // falloff; line width tracks alpha so brighter edges feel a touch
      // weightier.
      const VISIBILITY_RADIUS = ATTRACT_RADIUS * 1.5;
      const VISIBILITY_RADIUS_2 = VISIBILITY_RADIUS * VISIBILITY_RADIUS;
      const EDGE_REST = 0.18;
      const EDGE_BOOST = 0.7;

      ctx.lineCap = "round";
      for (let k = 0; k < edges.length; k++) {
        const [ai, bi] = edges[k];
        const a = nodes[ai];
        const b = nodes[bi];

        let alpha = EDGE_REST;
        if (cursor.active) {
          const mx = (a.x + b.x) * 0.5;
          const my = (a.y + b.y) * 0.5;
          const dx = cursor.x - mx;
          const dy = cursor.y - my;
          const d2 = dx * dx + dy * dy;
          if (d2 < VISIBILITY_RADIUS_2) {
            const f = 1 - Math.sqrt(d2) / VISIBILITY_RADIUS;
            alpha = EDGE_REST + EDGE_BOOST * f * f * f;
          }
        }

        ctx.strokeStyle = `rgba(255, 255, 255, ${alpha})`;
        ctx.lineWidth = 0.5 + 0.55 * Math.max(0, alpha - EDGE_REST);
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }

      // Soft cyan halo at the cursor — emphasizes the "flashlight" source.
      if (cursor.active) {
        const halo = ctx.createRadialGradient(
          cursor.x,
          cursor.y,
          0,
          cursor.x,
          cursor.y,
          VISIBILITY_RADIUS,
        );
        halo.addColorStop(0, "rgba(86, 194, 255, 0.08)");
        halo.addColorStop(0.4, "rgba(86, 194, 255, 0.025)");
        halo.addColorStop(1, "rgba(86, 194, 255, 0)");
        ctx.fillStyle = halo;
        ctx.fillRect(0, 0, width, height);
      }

      if (cursor.active || anyMoving) {
        rafRef.current = requestAnimationFrame(draw);
      } else {
        rafRef.current = null;
      }
    };

    const onResize = () => {
      seed();
      ensureRunning();
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
        if (isActive()) ensureRunning();
      },
      { threshold: 0 },
    );
    io.observe(canvas);

    const onVisibility = () => {
      pageVisible = !document.hidden;
      if (isActive()) ensureRunning();
    };
    document.addEventListener("visibilitychange", onVisibility);

    seed();
    rafRef.current = requestAnimationFrame(draw);

    window.addEventListener("resize", onResize);
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("mouseleave", onLeave);
    canvas.addEventListener("touchmove", onTouchMove, { passive: true });
    canvas.addEventListener("touchend", onTouchEnd);

    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("resize", onResize);
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mouseleave", onLeave);
      canvas.removeEventListener("touchmove", onTouchMove);
      canvas.removeEventListener("touchend", onTouchEnd);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [meshNodes]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: { xs: 420, md: 560 },
        cursor: "crosshair",
        maskImage:
          "radial-gradient(ellipse 70% 80% at 50% 50%, #000 55%, transparent 100%)",
        WebkitMaskImage:
          "radial-gradient(ellipse 70% 80% at 50% 50%, #000 55%, transparent 100%)",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          display: "block",
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
        }}
      >
        {meshNodes.map((n, i) => {
          const Icon = n.Icon;
          const fontSize = n.isStar ? 18 : 14;
          const opacity = n.isStar ? 0.92 : 0.66;
          return (
            <Box
              key={i}
              ref={(el: HTMLDivElement | null) => {
                iconRefs.current[i] = el;
              }}
              sx={{
                position: "absolute",
                left: `${n.pctX}%`,
                top: `${n.pctY}%`,
                transform: "translate(-50%, -50%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                willChange: "transform",
              }}
            >
              <Icon
                sx={{
                  fontSize,
                  color: `rgba(255, 255, 255, ${opacity})`,
                  filter:
                    "drop-shadow(0 1px 4px rgba(4, 5, 6, 0.85)) drop-shadow(0 0 6px rgba(4, 5, 6, 0.6))",
                  display: "block",
                }}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// =====================================================================
// Section
// =====================================================================
const Multiplayer: React.FC = () => {
  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        py: { xs: 10, md: 14 },
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          maxWidth: "var(--page-max-width)",
          mx: "auto",
          px: { xs: 3, sm: 5, md: 8 },
        }}
      >
        <Box
          component="h2"
          sx={{
            m: 0,
            maxWidth: "880px",
            fontFamily: fontInter,
            fontWeight: 600,
            fontSize: "clamp(2rem, 4.5vw, 3.5rem)",
            lineHeight: "var(--leading-heading)",
            letterSpacing: "var(--tracking-heading-lg)",
            color: "var(--color-snow)",
          }}
        >
          A network, not an app.
        </Box>
        <Box
          sx={{
            mt: 2,
            maxWidth: "780px",
            fontFamily: fontInter,
            fontSize: "var(--text-subheading)",
            lineHeight: "var(--leading-subheading)",
            letterSpacing: "var(--tracking-subheading)",
            color: "var(--color-slate-200)",
          }}
        >
          EON connects you, your coach, the brands you use, your wearables, and
          millions of agents. Plugged into every loop in your life.
        </Box>

        <Box sx={{ mt: { xs: 4, md: 6 } }}>
          <MeshCanvas />
        </Box>
      </Box>
    </Box>
  );
};

export default Multiplayer;
